import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { filter_data, get_overall_average } from "reports/Tag/calculations";
import { calculate_engagement_by_anchor } from "../../caluclate_engagement";
import {
  Container,
  SectionCard,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionBody,
  Button,
  SecondaryButton,
  Select,
  Label,
  FormGroup,
  JSONOutput,
  ToastContainer,
  Toast,
  LoadingSpinner,
  ErrorMsg
} from './StyledComponents';
import { computeOutcomeFactorCorrelations } from '../../organization_calculations';

// Utility function to parse JSON responses from GPT
function parseGPTJsonResponse(gptResponse) {
  try {
    let cleanedResponse = gptResponse.trim();
    
    if (cleanedResponse.startsWith('```json')) {
      cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
    }
    if (cleanedResponse.startsWith('```')) {
      cleanedResponse = cleanedResponse.replace(/^```\s*/, '');
    }
    if (cleanedResponse.endsWith('```')) {
      cleanedResponse = cleanedResponse.replace(/\s*```$/, '');
    }
    
    const parsedJson = JSON.parse(cleanedResponse);
    return parsedJson;
  } catch (error) {
    console.error("Error parsing GPT JSON response:", error);
    return {
      overview: "Error parsing response",
      quotes: [],
      insights: "Unable to parse insights from response",
      conclusion: "Response parsing failed"
    };
  }
}

// LLM API function - supports multiple models
async function callLLM(userPrompt, onTokenUpdate = null, model = "gpt-4.1") {
  try {
    let apiUrl, headers, requestBody;
    const scrambled = ["sk", "-", "svcacct","-", "i4THn1qxJ18sYOQJhRJLRH2wiTTxp1kx2_nQwzyeJtY4cyRpF16A3iv-6u7I3kDXqPNjVNpsRhT3BlbkFJmGpLTOFc-llkrsv0AutTgfauJQ5l0AcHyMNlZBPffbddq29MDb5n9vduzlaTrl3AZ4AcYl_usA"].join("");

    const code_to_use = scrambled;
    
    if (model === "grok-4-latest") {
      apiUrl = "https://api.x.ai/v1/chat/completions";
      headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer xai-LaVvLzCLdUTPt2ns9qNaPMz0BWJK8mwoHGlQlvZvUpeFbgZKyppsISdSnMIJbYDCKxdIAUeIT27SKlt9`,
      };
      requestBody = {
        model: "grok-4-latest",
        messages: [{ role: "user", content: userPrompt }],
        stream: false,
        temperature: 0.7
      };
    } else {
      apiUrl = "https://api.openai.com/v1/chat/completions";
      headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${code_to_use}`,
      };
      requestBody = {
        model: model,
        seed: 1,
        messages: [{ role: "user", content: userPrompt }],
        stream: false,
      };
    }

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`LLM API responded with status ${response.status}`);
    }

    const data = await response.json();
    const assistantMessage = data.choices?.[0]?.message?.content || "";
    const usage = data.usage || {};
    
    const tokenInfo = {
      prompt_tokens: usage.prompt_tokens || 0,
      completion_tokens: usage.completion_tokens || 0,
      total_tokens: usage.total_tokens || 0
    };

    if (onTokenUpdate) {
      onTokenUpdate(tokenInfo);
    }

    return {
      content: assistantMessage,
      tokens: tokenInfo
    };
  } catch (error) {
    console.error("Error calling LLM:", error);
    throw error;
  }
}

// Utility function to check if a value is a valid number
function isValidNumericResponse(value) {
  return value !== null && 
         value !== undefined && 
         value !== 'N/A' && 
         value !== 'n/a' && 
         value !== 'NA' && 
         value !== 'na' && 
         typeof value === 'number' && 
         !isNaN(value);
}

// Calculate the average scores per culture factor - from manager_v2.js
function calculateAverageScoresPerFactor(data) {
  const factorScores = {};
  data.forEach(entry => {
    const questions = entry.questions || [];
    questions.forEach(question => {
      const factor = question.factor;
      const dimension = question.id; // assuming "id" represents the dimension id
      const response = question.response;
      // Only process if we have valid numeric response (filter out 'N/A' values)
      if (factor != null && dimension != null && isValidNumericResponse(response)) {
        const key = `${factor}_${dimension}`;
        if (factorScores[key]) {
          factorScores[key].sum += response;
          factorScores[key].count += 1;
        } else {
          factorScores[key] = { sum: response, count: 1, factor, dimension };
        }
      }
    });
  });
  // Calculate averages for each factor key
  const averages = {};
  Object.keys(factorScores).forEach(key => {
    // Handle case where all responses were 'N/A' (count would be 0)
    if (factorScores[key].count > 0) {
      averages[key] = factorScores[key].sum / factorScores[key].count;
    } else {
      averages[key] = "No data"; // or could be null/undefined
    }
  });
  return averages;
}

// Calculate the average scores and high score percentage for employee outcomes - from manager_v2.js
function calculateAverageScoresPerOutcome(data, outcomeQuestions, selectedOutcomeId = null) {
  const results = {};
  
  if (!outcomeQuestions || !outcomeQuestions.questions) {
    return results;
  }
  
  // Filter outcome questions by selectedOutcomeId if provided (same as heatmap)
  const questionsToProcess = selectedOutcomeId 
    ? outcomeQuestions.questions.filter(question => String(question.id) === String(selectedOutcomeId))
    : outcomeQuestions.questions;
  
  questionsToProcess.forEach((question) => {
    let name = question.name;
    let filtered_responses = data
      .map((entry) =>
        entry?.employee_outcomes?.responses
          ?.filter((i) => {
            return (
              i.q == question.id &&
              (i?.clicked === true || i?.clicked === undefined)
            );
          })
          .map((i) => i?.response)
      )
      .flat()
      .filter((f) => !isNaN(f) && f !== undefined && f !== null);

    if (filtered_responses.length > 0) {
      const sum = filtered_responses.reduce((acc, val) => acc + val, 0);
      const average = sum / filtered_responses.length;
      
      // Calculate percentage of responses that are 7 or above
      const highScores = filtered_responses.filter(response => response >= 7);
      const highScorePercentage = (highScores.length / filtered_responses.length) * 100;
      
      // Calculate NPS components
      const promoters = filtered_responses.filter(response => response >= 9);
      const detractors = filtered_responses.filter(response => response <= 6);
      const promoterPercentage = (promoters.length / filtered_responses.length) * 100;
      const detractorPercentage = (detractors.length / filtered_responses.length) * 100;
      const npsScore = promoterPercentage - detractorPercentage;
      
      results[question.id] = {
        outcome_name: name,
        average_score: average,
        high_score_percentage: highScorePercentage,
        total_responses: filtered_responses.length,
        nps: {
          promoter_percentage: promoterPercentage,
          detractor_percentage: detractorPercentage,
          nps_score: npsScore
        }
      };
    } else {
      results[question.id] = {
        outcome_name: name,
        average_score: "No data",
        high_score_percentage: 0,
        total_responses: 0,
        nps: {
          promoter_percentage: 0,
          detractor_percentage: 0,
          nps_score: 0
        }
      };
    }
  });
  
  return results;
}

// Feedback extraction functions - from manager_v2.js
function filterFeedback(responses, factor, dimension) {
  if (!responses || !Array.isArray(responses)) return [];
  
  const feedbackList = [];
  responses.forEach(entry => {
    const questions = entry.feedback || [];
    questions.forEach(question => {
      if (question.factor === factor && question.id === dimension && question.feedback) {
        feedbackList.push({
          feedback: question.feedback,
          question: question.question || "",
          follow_up: question.follow_up || "",
          follow_up_question: question.follow_up_question || "",
          prompt_question: question.prompt_question || ""
        });
      }
    });
  });
  
  return feedbackList;
}

function getFactorFeedback(factorTuple, responses, options = {}) {
  if (!factorTuple) return "";
  const [factor, dimension] = factorTuple;
  
  // Filter the responses
  const feedbackList = filterFeedback(responses, factor, dimension);
  if (!feedbackList.length) return "";
  
  // Depending on options, choose a formatting strategy. For now, we use one format.
  return formatFeedback(feedbackList, options);
}

function formatFeedback(feedbackList, options = {}) {
  if (!feedbackList.length) return "";
  
  const { headerName } = options;
  let formatted = headerName
    ? `### Feedback from ${headerName} responses:\n\n`
    : "### Feedback from responses:\n\n";
  
  feedbackList.forEach(item => {
    // If prompt_question exists, handle it differently
    if (item.prompt_question) {
      formatted += "Follow up: " + item.prompt_question + "\n";
      formatted += "Response: " + (item.feedback || "") + "\n";
      formatted += "\n";
      return;
    }
    
    if (item.feedback) {
      formatted += "Question asked: " + (item.question || "") + "\n";
      formatted += "Response: " + item.feedback + "\n";
    }
    
    if (item.follow_up && item.follow_up_question) {
      formatted += "Follow up: " + item.follow_up_question + "\n";
      formatted += "Response: " + item.follow_up + "\n";
    }
    
    formatted += "\n";
  });
  
  formatted += "### END OF FEEDBACK \n\n";
  return formatted;
}

function getFactorName(questionsStructure, factorTuple) {
  if (!factorTuple) return null;
  const [factorId, dimensionId] = factorTuple;
  const dimensions = questionsStructure.dimensions || [];
  // Find the dimension whose id equals dimensionId+1 (adjust if necessary)
  const dimension = dimensions.find(dim => dim.id === dimensionId + 1);
  if (!dimension) return null;
  const factor = dimension.factors.find(fact => fact.id === factorId + 1);
  if (!factor) return null;
  return factor.title;
}

// --- Org vs Anchor key metrics helpers (adapted from manager_v3.js) ---
function applyReverseIfNeeded(questionItem, structure){
  try{
    const dim = structure?.dimensions?.find(d=>d.id === (questionItem.id+1));
    const fac = dim?.factors?.find(f=>f.id === (questionItem.factor+1));
    const qDef = fac?.questions?.[questionItem.q];
    if(qDef?.reverse){
      if(typeof questionItem.response === 'number'){
        return 10 - questionItem.response;
      }
    }
  }catch(e){}
  return questionItem.response;
}

function averageQuestionSet(data, dimIndex, factorIndex, structure){
  let sum = 0; let count = 0;
  data?.forEach(resp=>{
    resp?.questions?.forEach(q=>{
      if(q?.id === dimIndex && q?.factor === factorIndex){
        const val = applyReverseIfNeeded(q, structure);
        if(isValidNumericResponse(val)){ sum += val; count += 1; }
      }
    })
  })
  return count ? (sum / count) : 0;
}

function averageSingleQuestion(data, dimIndex, factorIndex, localIndex, structure){
  let sum = 0; let count = 0;
  data?.forEach(resp=>{
    resp?.questions?.forEach(q=>{
      if(q?.id === dimIndex && q?.factor === factorIndex && q?.q === localIndex){
        const val = applyReverseIfNeeded(q, structure);
        if(isValidNumericResponse(val)){ sum += val; count += 1; }
      }
    })
  })
  return count ? (sum / count) : 0;
}

function computeCultureOrgAnchorMetrics(orgData, anchorData, questionsStructure, lastOrgData = null, lastAnchorData = null){
  if(!questionsStructure?.dimensions) return [];
  const results = [];
  questionsStructure.dimensions.forEach((dimension, dimIndex)=>{
    dimension.factors.forEach((factor, factorIndex)=>{
      const orgAvg = averageQuestionSet(orgData, dimIndex, factorIndex, questionsStructure);
      const anchorAvg = averageQuestionSet(anchorData, dimIndex, factorIndex, questionsStructure);
      const hasLastOrg = Array.isArray(lastOrgData) && lastOrgData.length > 0;
      const hasLastAnchor = Array.isArray(lastAnchorData) && lastAnchorData.length > 0;
      const lastOrgAvg = hasLastOrg ? averageQuestionSet(lastOrgData, dimIndex, factorIndex, questionsStructure) : 0;
      const lastAnchorAvg = hasLastAnchor ? averageQuestionSet(lastAnchorData, dimIndex, factorIndex, questionsStructure) : 0;

      const questions = factor.questions.map((qObj, localIndex)=>{
        const orgQ = averageSingleQuestion(orgData, dimIndex, factorIndex, localIndex, questionsStructure);
        const anchorQ = averageSingleQuestion(anchorData, dimIndex, factorIndex, localIndex, questionsStructure);
        const item = { question: qObj.q, org_score: orgQ, anchor_score: anchorQ };
        if(hasLastOrg){ item.last_org_score = averageSingleQuestion(lastOrgData, dimIndex, factorIndex, localIndex, questionsStructure); }
        if(hasLastAnchor){ item.last_anchor_score = averageSingleQuestion(lastAnchorData, dimIndex, factorIndex, localIndex, questionsStructure); }
        return item;
      });

      const factorItem = { title: factor.title, org_score: orgAvg, anchor_score: anchorAvg, questions };
      factorItem.last_org_score = lastOrgAvg;
      factorItem.last_anchor_score = lastAnchorAvg;
      results.push(factorItem);
    })
  })
  return results;
}

function getChangeData(latestFactorScores, lastFactorScores, questions, filteredData = null, lastFilteredData = null){
  // Get the union of all factor keys (composite keys: "factor_dimension")
  const allFactorKeys = new Set([
    ...Object.keys(latestFactorScores),
    ...Object.keys(lastFactorScores)
  ]);

  const factorChanges = [];

  // Process each culture factor sequentially
  for (let key of allFactorKeys) {
    const parts = key.split("_");
    const factorId = parseInt(parts[0], 10);
    const dimensionId = parseInt(parts[1], 10);

    const factorName = getFactorName(questions, [factorId, dimensionId]);
    const latestScore =
      latestFactorScores[key] !== undefined
        ? latestFactorScores[key]
        : "No data";
    const lastScore =
      lastFactorScores[key] !== undefined ? lastFactorScores[key] : "No data";

    // Track the numeric score change
    const scoreChange =
      typeof latestScore === "number" && typeof lastScore === "number"
        ? latestScore - lastScore
        : 0;

    const latestFeedback = filteredData ? getFactorFeedback(
      [factorId, dimensionId],
      filteredData
    ) : "";
    const lastFeedback = lastFilteredData ? getFactorFeedback(
      [factorId, dimensionId],
      lastFilteredData
    ) : "";

    factorChanges.push({
      factor_id: key,
      factor_name: factorName,
      latest_score: latestScore,
      last_score: lastScore,
      score_change: scoreChange,
      latest_feedback: latestFeedback,
      last_feedback: lastFeedback
    });
  }

  return factorChanges;
}

const DSLTester = ({ dslConfig }) => {
  const [selectedModel, setSelectedModel] = useState('gpt-4.1');
  const [selectedAnchorParent, setSelectedAnchorParent] = useState('');
  const [selectedAnchorChild, setSelectedAnchorChild] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');
  const [tokenUsage, setTokenUsage] = useState({
    prompt_tokens: 0,
    completion_tokens: 0,
    total_tokens: 0,
    call_count: 0,
    input_cost: 0,
    output_cost: 0,
    total_cost: 0
  });

  // Redux data
  const core_data = useSelector((state) => state.audit?.core_data, shallowEqual);
  const raw_data = useSelector((state) => state.audit?.raw_data, shallowEqual);
  const debriefSchedules = useSelector((state) => state.debrief_schedule, shallowEqual);

  // Get anchor parents and children from core_data
  const anchorParents = core_data?.categories?.[0]?.categories || [];
  const selectedParentData = anchorParents.find(parent => parent.id === selectedAnchorParent);
  const anchorChildren = selectedParentData?.categories || [];

  // Model options with pricing
  const modelOptions = [
    {
      value: 'gpt-4.1',
      label: 'GPT-4.1',
      inputCostPerMillion: 2.00,
      outputCostPerMillion: 8.00
    },
    {
      value: 'gpt-4.1-mini',
      label: 'GPT-4.1 Mini',
      inputCostPerMillion: 0.40,
      outputCostPerMillion: 1.60
    },
    {
      value: 'gpt-4.1-nano',
      label: 'GPT-4.1 Nano',
      inputCostPerMillion: 0.10,
      outputCostPerMillion: 0.40
    },
    {
      value: 'gpt-5-nano',
      label: 'GPT-5 Nano',
      inputCostPerMillion: 0.05,
      outputCostPerMillion: 0.40
    },
    {
      value: 'gpt-5-mini',
      label: 'GPT-5 Mini',
      inputCostPerMillion: 0.25,
      outputCostPerMillion: 2.00
    },
    {
      value: 'grok-4-latest',
      label: 'Grok 4',
      inputCostPerMillion: 3.00,
      outputCostPerMillion: 15.00
    }
  ];

  const currentModel = modelOptions.find(model => model.value === selectedModel);

  // Set default anchor parent if not selected and data is available
  useEffect(() => {
    if (anchorParents.length > 0 && !selectedAnchorParent) {
      setSelectedAnchorParent(anchorParents[0].id);
    }
  }, [anchorParents, selectedAnchorParent]);

  // Reset anchor child when parent changes
  useEffect(() => {
    setSelectedAnchorChild('');
  }, [selectedAnchorParent]);

  const showToastMessage = (message, type = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Update token usage with cost calculation
  const updateTokenUsage = (newTokens) => {
    const inputCost = (newTokens.prompt_tokens / 1000000) * currentModel.inputCostPerMillion;
    const outputCost = (newTokens.completion_tokens / 1000000) * currentModel.outputCostPerMillion;
    const totalCost = inputCost + outputCost;

    setTokenUsage(prev => ({
      prompt_tokens: prev.prompt_tokens + newTokens.prompt_tokens,
      completion_tokens: prev.completion_tokens + newTokens.completion_tokens,
      total_tokens: prev.total_tokens + newTokens.total_tokens,
      call_count: prev.call_count + 1,
      input_cost: prev.input_cost + inputCost,
      output_cost: prev.output_cost + outputCost,
      total_cost: prev.total_cost + totalCost
    }));
  };

  // Process DSL configuration and generate test data
  const processDSL = async () => {
    if (!dslConfig || !raw_data || !core_data) {
      showToastMessage('Missing required data for DSL processing', 'error');
      return;
    }

    setLoading(true);
    setError(null);
    setTestResult(null);
    setTokenUsage({
      prompt_tokens: 0,
      completion_tokens: 0,
      total_tokens: 0,
      call_count: 0,
      input_cost: 0,
      output_cost: 0,
      total_cost: 0
    });

    try {
      const result = await processDSLConfiguration(dslConfig);
      setTestResult(result);
      showToastMessage('DSL processing completed successfully!', 'success');
    } catch (err) {
      console.error('DSL processing error:', err);
      setError(err.message || 'An error occurred during DSL processing');
      showToastMessage('DSL processing failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Main DSL processing function
  const processDSLConfiguration = async (config) => {
    // Find the selected anchor parent by ID
    const anchorParentData = core_data.categories[0].categories.find(cat => cat.id === selectedAnchorParent);
    
    if (!anchorParentData) {
      throw new Error(`Anchor parent with ID ${selectedAnchorParent} not found`);
    }

    const result = {
      includeData: config.includeData,
      selected_anchor_parent: {
        id: anchorParentData.id,
        name: anchorParentData.name
      },
      sections: {},
      anchor_reports: [],
      metadata: {
        processedAt: new Date().toISOString(),
        model: selectedModel,
        anchorParent: selectedAnchorParent,
        anchorChild: selectedAnchorChild,
        tokenUsage: tokenUsage
      }
    };

    // Process each section
    for (const [sectionName, section] of Object.entries(config.sections)) {
      const sectionResult = await processSection(section, sectionName, anchorParentData);
      result.sections[sectionName] = sectionResult;
      
      // If this is an anchor section, add the anchor reports to the main result
      if (section.type === 'anchor' && sectionResult.anchor_reports) {
        result.anchor_reports = sectionResult.anchor_reports;
      }
    }

    // --- Base metrics ---
    try {
      const orgDataAll = raw_data?.[0]?.responses || [];
      const lastOrgDataAll = raw_data?.[0]?.last || [];
      const overall_latest = get_overall_average(orgDataAll);
      const overall_last = get_overall_average(lastOrgDataAll) || 0;
      const overall_change = overall_latest - (overall_last || 0);

      result.overall_score = overall_latest;

      // Outcomes
      if (config.includeData?.outcomeScores && core_data.outcomeQuestions) {
        const orgLatestOutcomes = calculateAverageScoresPerOutcome(orgDataAll, core_data.outcomeQuestions);
        const anchorDataAll = selectedAnchorChild ? filter_data(orgDataAll, [{ id: selectedAnchorChild }]) : orgDataAll;
        const anchorLatestOutcomes = calculateAverageScoresPerOutcome(anchorDataAll, core_data.outcomeQuestions);
        const orgTotal = Object.values(orgLatestOutcomes).reduce((acc, v)=> acc + (typeof v?.average_score==='number' ? v.average_score : 0), 0) / (Object.keys(orgLatestOutcomes).length || 1);
        const anchorTotal = Object.values(anchorLatestOutcomes).reduce((acc, v)=> acc + (typeof v?.average_score==='number' ? v.average_score : 0), 0) / (Object.keys(anchorLatestOutcomes).length || 1);
        result.outcome_averages = {
          "Well-being": orgLatestOutcomes?.[Object.keys(orgLatestOutcomes)[0]]?.average_score,
          "Retention": orgLatestOutcomes?.[Object.keys(orgLatestOutcomes)[1]]?.average_score,
          "Engagement": orgLatestOutcomes?.[Object.keys(orgLatestOutcomes)[2]]?.average_score
        };
        result.outcomeScores = {
          org: { total: orgTotal, questions: Object.fromEntries(Object.values(core_data.outcomeQuestions.questions || {}).flatMap((s)=>s.questions).map(q=>[q.section_name || q.name || q.q, orgLatestOutcomes?.[q.id]?.average_score]).filter(([k,v])=>k!==undefined)) },
          anchor: { total: anchorTotal, questions: Object.fromEntries(Object.values(core_data.outcomeQuestions.questions || {}).flatMap((s)=>s.questions).map(q=>[q.section_name || q.name || q.q, anchorLatestOutcomes?.[q.id]?.average_score]).filter(([k,v])=>k!==undefined)) }
        };
      }

      // Participation (if available)
      if (debriefSchedules?.culture_audit_reports?.response) {
        const parentId = selectedAnchorParent;
        const pr = calculate_engagement_by_anchor(
          debriefSchedules.culture_audit_reports.response,
          parentId,
          core_data.categories,
          core_data.orgCategories
        );
        const total = pr.reduce((a,b)=> a + (b.total || 0), 0);
        const responded = pr.reduce((a,b)=> a + (b.completed || 0), 0);
        result.participation = { responded, sent: total, rate: total ? responded/total : 0 };
      }

      // Selected anchor child info
      if (selectedAnchorChild) {
        const childNode = (anchorParents.find(p=>p.id===selectedAnchorParent)?.categories || []).find(c=>c.id===selectedAnchorChild);
        if (childNode) {
          result.selected_anchor_child = { id: childNode.id, name: childNode.name };
        }
      }

      // Question scores (org vs anchor)
      const anchorDataAll = selectedAnchorChild ? filter_data(raw_data?.[0]?.responses || [], [{ id: selectedAnchorChild }]) : (raw_data?.[0]?.responses || []);
      const question_scores = computeCultureOrgAnchorMetrics(raw_data?.[0]?.responses || [], anchorDataAll, core_data.questions, raw_data?.[0]?.last || [], selectedAnchorChild ? filter_data(raw_data?.[0]?.last || [], [{ id: selectedAnchorChild }]) : (raw_data?.[0]?.last || []));
      if (config.includeData?.question_scores) {
        result.question_scores = question_scores;
      }

      // Global standards
      const standards = core_data?.standards;
      if (standards?.response) {
        const pillars = standards.response.pillars || [];
        result.factor_global_standards = pillars.map(p => ({
          id: p.id,
          std: p.std,
          factor: p.factor,
          average: p.average,
          overall: p.overall
        }));
        if (standards.response.overall) {
          const ogs = standards.response.overall;
          result.overall_global_standard = {
            n: ogs.n,
            std: ogs.std,
            average: ogs.average
          };
        }
      }
    } catch (e) {
      console.warn('Base metrics computation failed', e);
    }

    return result;
  };

  // Process individual section
  const processSection = async (section, sectionName, anchorParentData) => {
    if (section.type === 'anchor') {
      return await processAnchorSection(section, sectionName, anchorParentData);
    } else {
      return await processRegularSection(section, sectionName);
    }
  };

  // Process anchor section with real data like manager_v2.js
  const processAnchorSection = async (section, sectionName, anchorParentData) => {
    const anchorReports = [];
    
    // Iterate through each of the anchor options in the selected anchor parent
    const anchorOptions = anchorParentData.options || anchorParentData.categories || [];
    
    for (const option of anchorOptions) {
      const anchorID = option.id;
      const anchorName = option.name;
      
      // Filter data for this specific anchor - using the same logic as manager_v2.js
      const filteredData = filter_data(raw_data[0].responses, [{id: anchorID}]);
      const lastFilteredData = filter_data(raw_data[0].last, [{id: anchorID}]);
      
      // Calculate factor scores for this anchor
      const latestFactorScores = calculateAverageScoresPerFactor(filteredData);
      const lastFactorScores = calculateAverageScoresPerFactor(lastFilteredData);
      
      // Calculate outcome scores for this anchor
      let outcomeScores = {};
      if (core_data.outcomeQuestions && section.config?.includeOutcomeScores) {
        const latestOutcomeScores = {};
        const lastOutcomeScores = {};
        
        core_data.outcomeQuestions.questions.forEach(outcomeSection => {
          const latestSectionScores = calculateAverageScoresPerOutcome(filteredData, core_data.outcomeQuestions, outcomeSection.id);
          const lastSectionScores = calculateAverageScoresPerOutcome(lastFilteredData, core_data.outcomeQuestions, outcomeSection.id);
          
          Object.assign(latestOutcomeScores, latestSectionScores);
          Object.assign(lastOutcomeScores, lastSectionScores);
        });
        
        outcomeScores = {
          latest: latestOutcomeScores,
          previous: lastOutcomeScores
        };
      }
      
      // Get overall scores
      const overallAverage = get_overall_average(filteredData);
      const lastOverallAverage = get_overall_average(lastFilteredData);
      
      // Get change data with feedback
      const changeDataWithFeedback = getChangeData(latestFactorScores, lastFactorScores, core_data.questions, filteredData, lastFilteredData);
      
      // Collect all feedback for this anchor
      const allFeedback = changeDataWithFeedback
        .map(factor => factor.latest_feedback)
        .filter(feedback => feedback && feedback.trim())
        .join('\n\n');
      
      // Process LLM actions for this anchor
      let llmResults = {};
      if (section.llmActions && Object.keys(section.llmActions).length > 0) {
        for (const [actionName, action] of Object.entries(section.llmActions)) {
          let prompt = action.prompt;
          
          // Replace variables with real data
          const variables = {
            '{{feedback}}': allFeedback || `No feedback available for ${anchorName}`,
            '{{scores}}': JSON.stringify(changeDataWithFeedback.map(f => ({
              name: f.factor_name,
              score: f.latest_score,
              anchorName: anchorName
            }))),
            '{{factorNames}}': JSON.stringify(changeDataWithFeedback.map(f => f.factor_name)),
            '{{name}}': changeDataWithFeedback.map(f => f.factor_name).join(', '),
            '{{change}}': JSON.stringify(changeDataWithFeedback.map(f => ({
              name: f.factor_name,
              change: f.score_change,
              anchorName: anchorName
            }))),
            '{{benchmark}}': JSON.stringify(changeDataWithFeedback.map(f => ({
              name: f.factor_name,
              benchmark: f.latest_score, // Using score as benchmark for now
              anchorName: anchorName
            }))),
            '{{outcomeScores}}': JSON.stringify(outcomeScores)
          };
          
          for (const [variable, value] of Object.entries(variables)) {
            prompt = prompt.replace(new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
          }
          
          try {
            const response = await callLLM(prompt, updateTokenUsage, selectedModel);
            
            let result = response.content;
            if (action.formatAsJson) {
              try {
                result = parseGPTJsonResponse(response.content);
              } catch (error) {
                result = {
                  error: "Failed to parse JSON response",
                  rawResponse: response.content
                };
              }
            }
            
            llmResults[actionName] = {
              action: action.name || actionName,
              prompt: prompt,
              feedbackType: action.feedbackType,
              formatAsJson: action.formatAsJson,
              result: result,
              status: 'success',
              tokens: response.tokens
            };
          } catch (error) {
            llmResults[actionName] = {
              error: error.message,
              status: 'failed'
            };
          }
        }
      }
      
      // Build the anchor report
      const anchorReport = {
        anchor_id: anchorID,
        anchor_name: anchorName,
        overall_score: overallAverage,
        previous_overall_score: lastOverallAverage,
        score_change: (overallAverage - lastOverallAverage) * 10, // Convert to percentage
        total_feedback_count: filteredData.length,
        all_factors: changeDataWithFeedback.map(factor => ({
          factor_name: factor.factor_name,
          latest_score: factor.latest_score,
          last_score: factor.last_score,
          score_change: factor.score_change * 10 // Convert to percentage
        })),
        outcome_scores: outcomeScores,
        feedback_summary: allFeedback,
        llm_results: llmResults
      };
      
      anchorReports.push(anchorReport);
    }
    
    return {
      type: 'anchor',
      config: section.config,
      anchor_reports: anchorReports,
      total_anchors_processed: anchorReports.length
    };
  };

  // Process regular (non-anchor) section
  const processRegularSection = async (section, sectionName) => {
    const sectionResult = {
      type: section.type,
      config: section.config,
      data: [],
      llmResults: {}
    };

    // Get data based on section type
    const sectionData = await getSectionData(section, sectionName);

  // Process LLM actions if any
  if (section.llmActions && Object.keys(section.llmActions).length > 0 && (section.type === 'lowestScore' || section.type === 'highestScore' || section.type === 'factorDistributionHighest' || section.type === 'factorDistributionLowest')) {
      const enriched = await processPerFactorLLMActions(section.llmActions, sectionData);
      sectionResult.data = enriched;
      sectionResult.llmResults = undefined;
    } else {
      sectionResult.data = sectionData;
      if (section.llmActions && Object.keys(section.llmActions).length > 0) {
        sectionResult.llmResults = await processLLMActions(section.llmActions, sectionData);
      }
    }

    return sectionResult;
  };

  // Get data for different section types using real calculation functions
  const getSectionData = async (section, sectionName) => {
    switch (section.type) {
      case 'anchor':
        return await getAnchorData(section);
      case 'lowestScore':
        return await getLowestScoreData(section);
      case 'highestScore':
        return await getHighestScoreData(section);
      case 'factorDistributionHighest':
        return await getFactorDistributionByHighest(section);
      case 'factorDistributionLowest':
        return await getFactorDistributionByLowest(section);
      case 'biggestPositiveChange':
        return await getBiggestPositiveChangeData(section);
      case 'biggestNegativeChange':
        return await getBiggestNegativeChangeData(section);
      case 'factorScoreByHighest':
        return await getFactorScoreByHighestData(section);
      case 'outcomeForEach':
        return await getOutcomeForEachData(section);
      case 'outcomeInsight':
        return await getOutcomeInsightData(section);
      default:
        return [];
    }
  };

  // Get anchor data - simplified since we handle anchors specially now
  const getAnchorData = async (section) => {
    return []; // Anchor processing is handled in processAnchorSection
  };

  // Get lowest score data using real calculations
  const getLowestScoreData = async (section) => {
    const num = section.config?.num || 3;
    
    // Get factor scores from real data using the proper data structure
    const factorScores = calculateAverageScoresPerFactor(raw_data[0].responses);
    const lastFactorScores = calculateAverageScoresPerFactor(raw_data[0].last);
    
    // Sort by score and get lowest
    const sortedFactors = Object.entries(factorScores)
      .filter(([_, score]) => isValidNumericResponse(score))
      .sort(([_, a], [__, b]) => a - b)
      .slice(0, num);

    return sortedFactors.map(([factorId, score]) => {
      const parts = factorId.split("_");
      const factorIdNum = parseInt(parts[0], 10);
      const dimensionId = parseInt(parts[1], 10);
      const factorName = getFactorName(core_data.questions, [factorIdNum, dimensionId]);
      const previous = typeof lastFactorScores[factorId] === 'number' ? lastFactorScores[factorId] : null;
      const change = typeof score === 'number' && typeof previous === 'number' ? (score - previous) : 0;
      const feedback = getFactorFeedback([factorIdNum, dimensionId], raw_data[0].responses);
      
      return {
        id: factorId,
        name: factorName || `Factor ${factorId}`,
        score: score,
        last_score: previous,
        change: change,
        benchmark: score, // Using current score as benchmark for now
        factorScore: score,
        feedback: feedback
      };
    });
  };

  // Get highest score data using real calculations
  const getHighestScoreData = async (section) => {
    const num = section.config?.num || 3;
    
    const factorScores = calculateAverageScoresPerFactor(raw_data[0].responses);
    const lastFactorScores = calculateAverageScoresPerFactor(raw_data[0].last);
    
    const sortedFactors = Object.entries(factorScores)
      .filter(([_, score]) => isValidNumericResponse(score))
      .sort(([_, a], [__, b]) => b - a)
      .slice(0, num);

    return sortedFactors.map(([factorId, score]) => {
      const parts = factorId.split("_");
      const factorIdNum = parseInt(parts[0], 10);
      const dimensionId = parseInt(parts[1], 10);
      const factorName = getFactorName(core_data.questions, [factorIdNum, dimensionId]);
      const previous = typeof lastFactorScores[factorId] === 'number' ? lastFactorScores[factorId] : null;
      const change = typeof score === 'number' && typeof previous === 'number' ? (score - previous) : 0;
      const feedback = getFactorFeedback([factorIdNum, dimensionId], raw_data[0].responses);
      
      return {
        id: factorId,
        name: factorName || `Factor ${factorId}`,
        score: score,
        last_score: previous,
        change: change,
        benchmark: score,
        factorScore: score,
        feedback: feedback
      };
    });
  };

  // Get biggest positive/negative change data - simplified since we handle this in anchor processing
  const getBiggestPositiveChangeData = async (section) => {
    return []; // Change analysis is handled in anchor processing with real data
  };

  const getBiggestNegativeChangeData = async (section) => {
    return []; // Change analysis is handled in anchor processing with real data
  };

  const getFactorScoreByHighestData = async (section) => {
    return []; // Factor scoring is handled in anchor processing with real data
  };

  // Get outcome insight data using real outcome calculations
  const getOutcomeInsightData = async (section) => {
    const outcomeId = section?.config?.outcomeId;
    if (!outcomeId) return [];

    const latestOutcomeScores = calculateAverageScoresPerOutcome(raw_data[0].responses, core_data.outcomeQuestions, outcomeId);
    const lastOutcomeScores = calculateAverageScoresPerOutcome(raw_data[0].last, core_data.outcomeQuestions, outcomeId);

    // Merge structure preserves the one outcome section keyed by id
    const merged = {
      latest: latestOutcomeScores,
      previous: lastOutcomeScores
    };

    // Return as a simple array so LLM variables like {{scores}} can still serialize
    return [{
      id: String(outcomeId),
      name: latestOutcomeScores?.[outcomeId]?.outcome_name || `Outcome ${outcomeId}`,
      score: latestOutcomeScores?.[outcomeId]?.average_score ?? 'No data',
      change: (typeof latestOutcomeScores?.[outcomeId]?.average_score === 'number' && typeof lastOutcomeScores?.[outcomeId]?.average_score === 'number')
        ? latestOutcomeScores[outcomeId].average_score - lastOutcomeScores[outcomeId].average_score
        : 0,
      benchmark: latestOutcomeScores?.[outcomeId]?.average_score ?? 0,
      outcomeScores: merged
    }];
  };

  // Get outcome (for each) data across all outcomes
  const getOutcomeForEachData = async (section) => {
    const latestOutcomeScores = calculateAverageScoresPerOutcome(raw_data[0].responses, core_data.outcomeQuestions);
    const lastOutcomeScores = calculateAverageScoresPerOutcome(raw_data[0].last, core_data.outcomeQuestions);

    // Optionally compute correlations between each outcome and all factors
    const includeCorr = !!section?.config?.includeFactorCorrelations;
    const correlationsByOutcome = includeCorr
      ? computeOutcomeFactorCorrelations(raw_data[0].responses, core_data.outcomeQuestions, core_data.questions)
      : {};

    const list = (core_data?.outcomeQuestions?.questions || []).map(sectionDef => {
      const outcomeId = sectionDef.id;
      const latest = latestOutcomeScores?.[outcomeId]?.average_score ?? 'No data';
      const previous = lastOutcomeScores?.[outcomeId]?.average_score ?? null;
      const change = (typeof latest === 'number' && typeof previous === 'number') ? (latest - previous) : 0;
      return {
        id: String(outcomeId),
        name: sectionDef.name || `Outcome ${outcomeId}`,
        score: latest,
        last_score: previous,
        change: change,
        benchmark: typeof latest === 'number' ? latest : 0,
        factor_correlations: includeCorr ? (correlationsByOutcome[outcomeId] || []) : undefined
      };
    });

    return list;
  };

  // Factor distribution helpers
  function getDistributionBands() {
    const bands = dslConfig?.includeData?.score_distribution_bands;
    const low = Array.isArray(bands?.low) ? bands.low : [0, 4];
    const neutral = Array.isArray(bands?.neutral) ? bands.neutral : [5, 6];
    const high = Array.isArray(bands?.high) ? bands.high : [7, 10];
    return { low, neutral, high };
  }

  function computeDistributionPercentages(values) {
    const { low, neutral, high } = getDistributionBands();
    const nums = (values || []).filter(v => typeof v === 'number' && !isNaN(v));
    const n = nums.length || 1;
    const low_pct = (nums.filter(v => v >= low[0] && v <= low[1]).length / n) * 100;
    const neutral_pct = (nums.filter(v => v >= neutral[0] && v <= neutral[1]).length / n) * 100;
    const high_pct = (nums.filter(v => v >= high[0] && v <= high[1]).length / n) * 100;
    return { low_pct, neutral_pct, high_pct };
  }

  async function getAllFactorValues() {
    const map = {};
    const list = raw_data?.[0]?.responses || [];
    list.forEach(resp => {
      const items = resp?.questions || [];
      items.forEach(q => {
        if (q?.id == null || q?.factor == null) return;
        const v = q?.response;
        if (!isValidNumericResponse(v)) return;
        const key = `${q.factor}_${q.id}`;
        if (!map[key]) map[key] = [];
        map[key].push(v);
      });
    });
    return map;
  }

  const getFactorDistributionByHighest = async (section) => {
    const num = section?.config?.num || 3;
    const factorValues = await getAllFactorValues();
    const items = Object.keys(factorValues).map(key => {
      const [f, d] = key.split('_').map(n => parseInt(n, 10));
      const name = getFactorName(core_data.questions, [f, d]) || `Factor ${key}`;
      const dist = computeDistributionPercentages(factorValues[key]);
      return {
        id: key,
        name,
        score: dist.high_pct,
        low_pct: dist.low_pct,
        neutral_pct: dist.neutral_pct,
        high_pct: dist.high_pct
      };
    });
    return items.sort((a, b) => b.score - a.score).slice(0, num);
  };

  const getFactorDistributionByLowest = async (section) => {
    const num = section?.config?.num || 3;
    const factorValues = await getAllFactorValues();
    const items = Object.keys(factorValues).map(key => {
      const [f, d] = key.split('_').map(n => parseInt(n, 10));
      const name = getFactorName(core_data.questions, [f, d]) || `Factor ${key}`;
      const dist = computeDistributionPercentages(factorValues[key]);
      return {
        id: key,
        name,
        score: dist.low_pct,
        low_pct: dist.low_pct,
        neutral_pct: dist.neutral_pct,
        high_pct: dist.high_pct
      };
    });
    return items.sort((a, b) => b.score - a.score).slice(0, num);
  };

  // Simplified process LLM actions for non-anchor sections
  const processLLMActions = async (llmActions, data) => {
    const results = {};
    
    for (const [actionName, action] of Object.entries(llmActions)) {
      try {
        let prompt = action.prompt;
        
        // Replace basic variables
        const variables = {
          '{{scores}}': JSON.stringify(data.map(item => ({ name: item.name, score: item.score }))),
          '{{factorNames}}': JSON.stringify(data.map(item => item.name)),
          '{{name}}': data.map(item => item.name).join(', '),
          '{{outcomeScores}}': JSON.stringify(data?.[0]?.outcomeScores || {})
        };
        
        for (const [variable, value] of Object.entries(variables)) {
          prompt = prompt.replace(new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
        }
        
        const response = await callLLM(prompt, updateTokenUsage, selectedModel);
        
        let result = response.content;
        if (action.formatAsJson) {
          try {
            result = parseGPTJsonResponse(response.content);
          } catch (error) {
            result = {
              error: "Failed to parse JSON response",
              rawResponse: response.content
            };
          }
        }
        
        results[actionName] = {
          action: action.name || actionName,
          prompt: prompt,
          result: result,
          status: 'success',
          tokens: response.tokens
        };
      } catch (error) {
        results[actionName] = {
          error: error.message,
          status: 'failed'
        };
      }
    }

    return results;
  };

  // Per-factor LLM processing for factor lists (lowest/highest)
  const processPerFactorLLMActions = async (llmActions, data) => {
    const perFactorResults = [];
    for (const item of data) {
      const llm_output = {};
      for (const [actionName, action] of Object.entries(llmActions)) {
        try {
          let prompt = action.prompt;
          const variables = {
            '{{factorId}}': item.id,
            '{{factorName}}': item.name,
            '{{name}}': item.name,
            '{{score}}': typeof item.score === 'number' ? item.score.toString() : String(item.score),
            '{{currentScore}}': typeof item.score === 'number' ? item.score.toString() : String(item.score),
            '{{previousScore}}': typeof item.last_score === 'number' ? item.last_score.toString() : String(item.last_score ?? ''),
            '{{change}}': typeof item.change === 'number' ? item.change.toString() : String(item.change ?? ''),
            '{{benchmark}}': typeof item.benchmark === 'number' ? item.benchmark.toString() : String(item.benchmark ?? ''),
            '{{feedback}}': item.feedback || ''
          };
          for (const [variable, value] of Object.entries(variables)) {
            prompt = prompt.replace(new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
          }
          const response = await callLLM(prompt, updateTokenUsage, selectedModel);
          let result = response.content;
          if (action.formatAsJson) {
            try {
              result = parseGPTJsonResponse(response.content);
            } catch (error) {
              result = { error: 'Failed to parse JSON response', rawResponse: response.content };
            }
          }
          llm_output[actionName] = {
            action: action.name || actionName,
            feedbackType: action.feedbackType,
            formatAsJson: !!action.formatAsJson,
            result: result,
            status: 'success',
            tokens: response.tokens,
            errors: []
          };
        } catch (error) {
          llm_output[actionName] = {
            action: action.name || actionName,
            feedbackType: action.feedbackType,
            formatAsJson: !!action.formatAsJson,
            result: null,
            status: 'failed',
            tokens: {},
            errors: [error.message]
          };
        }
      }
      const { feedback, ...rest } = item; // do not include raw feedback in output
      perFactorResults.push({ ...rest, llm_output });
    }
    return perFactorResults;
  };



  const canProcess = dslConfig && raw_data && core_data && selectedAnchorParent;

  return (
    <Container>
      <h2>DSL Configuration Tester</h2>
      
      <SectionCard>
        <SectionHeader>
          <SectionTitle>Test Configuration</SectionTitle>
        </SectionHeader>
        <SectionBody>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormGroup>
              <Label>LLM Model</Label>
              <Select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={loading}
              >
                {modelOptions.map(model => (
                  <option key={model.value} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Anchor Parent</Label>
              <Select
                value={selectedAnchorParent}
                onChange={(e) => setSelectedAnchorParent(e.target.value)}
                disabled={loading || anchorParents.length === 0}
              >
                {anchorParents.length === 0 ? (
                  <option value="">No anchor parents available</option>
                ) : (
                  anchorParents.map(parent => (
                    <option key={parent.id} value={parent.id}>
                      {parent.name || `Anchor Parent ${parent.id}`}
                    </option>
                  ))
                )}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Anchor Child (Optional)</Label>
              <Select
                value={selectedAnchorChild}
                onChange={(e) => setSelectedAnchorChild(e.target.value)}
                disabled={loading || anchorChildren.length === 0}
              >
                <option value="">All Children</option>
                {anchorChildren.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name || `Child ${child.id}`}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>&nbsp;</Label>
              <Button 
                onClick={processDSL}
                disabled={!canProcess || loading}
                style={{ width: '100%' }}
              >
                {loading ? 'Processing...' : 'Process DSL Configuration'}
              </Button>
            </FormGroup>
          </div>
        </SectionBody>
      </SectionCard>

      {/* Token Usage Display */}
      {(loading || tokenUsage.call_count > 0) && (
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Token Usage</SectionTitle>
          </SectionHeader>
          <SectionBody>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
              <div>
                <strong>API Calls:</strong> {tokenUsage.call_count}
              </div>
              <div>
                <strong>Input Tokens:</strong> {tokenUsage.prompt_tokens.toLocaleString()}
              </div>
              <div>
                <strong>Output Tokens:</strong> {tokenUsage.completion_tokens.toLocaleString()}
              </div>
              <div>
                <strong>Total Tokens:</strong> {tokenUsage.total_tokens.toLocaleString()}
              </div>
              <div>
                <strong>Total Cost:</strong> ${tokenUsage.total_cost.toFixed(4)}
              </div>
            </div>
          </SectionBody>
        </SectionCard>
      )}

      {/* Error Display */}
      {error && (
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Error</SectionTitle>
          </SectionHeader>
          <SectionBody>
            <ErrorMsg>{error}</ErrorMsg>
          </SectionBody>
        </SectionCard>
      )}

      {/* Loading Spinner */}
      {loading && (
        <SectionCard>
          <SectionBody>
            <LoadingSpinner />
          </SectionBody>
        </SectionCard>
      )}

      {/* Results Display */}
      {testResult && (
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Test Results</SectionTitle>
          </SectionHeader>
          <SectionBody>
            <JSONOutput>{JSON.stringify(testResult, null, 2)}</JSONOutput>
          </SectionBody>
        </SectionCard>
      )}

      {/* Toast Notifications */}
      {showToast && (
        <ToastContainer>
          <Toast type={toastType}>
            <span>{toastType.toUpperCase()}</span>
            <span>{toastMessage}</span>
          </Toast>
        </ToastContainer>
      )}
    </Container>
  );
};

export default DSLTester; 